import { useEffect, useState, useRef, useMemo } from "react";
import Cookies from "js-cookie";
import isEmpty from "lodash/isEmpty";

import { FaQuestionCircle, FaExclamationCircle } from "react-icons/fa";
import Skeleton from "components/ui/Preloader/Skeleton";
import {
	Autocomplete,
	Box,
	Button,
	Divider,
	Flex,
	Grid,
	Group,
	Modal,
	NumberInput,
	Pill,
	PillsInput,
	Skeleton as MantineSkeleton,
	Stack,
	Text,
	TextInput,
	ThemeIcon,
	Tooltip,
} from "@mantine/core";
import TableBounds from "./TableBounds";
import LicenseManagersSection from "./LicenseManagersSection";
import ModalButtons from "components/ui/Modal/ModalButtons";
import { useForm } from "@mantine/form";

import { FormData, ViewLicenseProps, LicenseManager } from "types/module/License";
import { useLicenseNotificationUpdate, useLicenseAliasUpdate, useLicensePool, useOutboundAllocations, useRemoveLicenseManager, useUnlinkContractManager, useLinkContractManager } from "hooks/useLicense";
import { MSG } from "constants/msg";
import { isNil } from "lodash";
import { getManagerEmail } from "helpers";

const ViewLicense: React.FC<ViewLicenseProps> = ({
	serial,
	isEditable,
	isOpenLicenseModal,
	handleFormChange,
	setHasUnsavedChanges,
	availableManagers = [],
	onAddMoreManagers,
	onDirectAssignManagers,
	assignManagersSuccess,
	isAssigningManagers = false,
	availableAccounts = [],
	currentCMAssignment = '',
	linkedPoolId = '',
	parentPool = '',
	isUpdatingCM = false,
	setIsUpdatingCM,
	isLicensesFetching = false,
	disableCMAssignmentForPool,
	accountAdmins = [],
	onModalClose,
	onLMOperationChange,
	isAllowedAllocation = false,
}) => {
	const aliasMaxLength = 255; // based on PAM API error once 255 is exceeded
	const hadOperationsRef = useRef(false);
	const isNotificationUpdateFromLMOperation = useRef(false);
	const lastLMOperationWasAssignment = useRef(false);
	const lastNotificationUpdateWasManual = useRef(false);
	const submittedAliasRef = useRef<string>("");

	// Pagination state for outbound allocations
	const [outboundPage, setOutboundPage] = useState<number>(1);
	const [outboundSize] = useState<number>(5);

	const [dateFilters, setDateFilters] = useState<{ date_gte?: string; date_lte?: string }>({});

	const [licenseManagers, setLicenseManagers] = useState<LicenseManager[]>(() => {
		const managers = serial?.licenseManagers || [];
		return managers.filter(m => !m.isAdmin);
	});

	const [admins, setAdmins] = useState<LicenseManager[]>(accountAdmins);
	const [managerToRemove, setManagerToRemove] = useState<string | null>(null);
	const [loadingRemoveManagerId, setLoadingRemoveManagerId] = useState<string | null>(null);
	const [isRemovingAll, setIsRemovingAll] = useState<boolean>(false);
	const [pendingManagersToAdd, setPendingManagersToAdd] = useState<LicenseManager[]>([]);
	const [selectedCM, setSelectedCM] = useState<string>(currentCMAssignment);
	// After each successful CM operation in this modal session, lastCommittedCM is updated
	// to the new value.  The save button compares against this (not the stale prop) so that
	// a second change inside the same open modal is correctly detected/enabled.
	const [lastCommittedCM, setLastCommittedCM] = useState<string>(currentCMAssignment);
	const hadCMOperationsRef = useRef(false);
	const pendingLinkAccountName = useRef<string>('');
	// Stores the linkId returned by the most recent successful link API call.
	// The prop linkedPoolId is stale until modal close re-fetches /licenses, so
	// any same-session unlink must use this ref instead.
	const committedLinkIdRef = useRef<string>('');

	// Separate state for alias (not part of form)
	const [aliasValue, setAliasValue] = useState<string>("");
	const [aliasDefault, setAliasDefault] = useState<string>("");
	const [aliasError, setAliasError] = useState<string | null>(null);
	const [showAliasResetConfirm, setShowAliasResetConfirm] = useState<boolean>(false);

	// All admin emails that should be non-removable in notification contacts - LM will be removed as a separate call after unassignment, so they are handled in the form separately
	const lmEmails = useMemo(() => {
		const allManagers = [...licenseManagers, ...admins];
		return allManagers.map(m => getManagerEmail(m)?.toLowerCase()).filter(Boolean) as string[];
	}, [licenseManagers, admins]);

	const {
		mutate: unlinkCMMutate,
		isSuccess: isUnlinkSuccess,
		isError: isUnlinkError,
	} = useUnlinkContractManager();

	const {
		mutateAsync: linkCMMutateAsync,
		isSuccess: isLinkSuccess,
		isError: isLinkError,
	} = useLinkContractManager();

	const {
		data: serialData,
		isFetching: isSerialDataLoading,
		refetch: serialDataRefetch,
	} = useLicensePool(
		typeof serial.serialNumber === 'string' ? serial.serialNumber : serial.serialNumber?.poolId,
		!!parentPool
	);

	// Separate hook for outbound allocations with pagination
	const poolUuid = typeof serial.serialNumber === 'string' ? serial.serialNumber : serial.serialNumber?.poolId;
	const {
		data: outboundData,
		isFetching: isOutboundLoading,
	} = useOutboundAllocations(poolUuid, {
		page: outboundPage,
		size: outboundSize,
		...dateFilters,
	});

	const {
		mutate: notificationUpdateMutate,
		data: notificationUpdateData,
		isSuccess: notificationUpdateSuccess,
		isError: notificationUpdateError,
		isPending: isUpdatingNotification,
	} = useLicenseNotificationUpdate();

	const {
		mutate: aliasUpdateMutate,
		data: aliasUpdateData,
		isSuccess: aliasUpdateSuccess,
		isError: aliasUpdateError,
		isPending: isUpdatingAlias,
	} = useLicenseAliasUpdate();

	const {
		mutate: removeManagerMutate,
		isSuccess: removeManagerSuccess,
		isError: removeManagerError,
	} = useRemoveLicenseManager();

	const form = useForm({
		initialValues: {
			threshold: serial.pool_balance_threshold,
			contacts: (serial.notification_contacts && serial.notification_contacts.split(";")) || [],
		},
		validate: {
			threshold: (value) => {
				if (value === 0) return null;
				if (!value) return MSG.VALIDATE.REQUIRED('Notification Threshold');
				return /^\d+$/.test(value.toString()) ? null : "Only whole numbers are allowed";
			},
			contacts: (value) => {
				if (form.values.threshold > 0 && value.length === 0) {
					return MSG.VALIDATE.REQUIRED("Notification Contacts");
				}
				return value.every((tag) =>
					/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(tag),
				) ? null : "All contacts must be valid email addresses";
			},
		},
	});

	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [isLoadingContacts, setIsLoadingContacts] = useState<boolean>(false);
	const [isInitialModalLoad, setIsInitialModalLoad] = useState<boolean>(true);
	const [hasNotificationFormChanges, setHasNotificationFormChanges] = useState<boolean>(false);
	const contactsError = form.errors.contacts;
	const thresholdValue = form.values.threshold;

	const prevIsOpenLicenseModalRef = useRef(isOpenLicenseModal);

	useEffect(() => {
		const wasModalClosed = !prevIsOpenLicenseModalRef.current;
		const isModalNowOpen = isOpenLicenseModal;
		const modalJustOpened = wasModalClosed && isModalNowOpen;

		if (modalJustOpened && serial?.licenseManagers) {
			const managers = serial.licenseManagers;
			const nonAdmins = managers.filter(m => !m.isAdmin);
			setLicenseManagers(nonAdmins);
		}

		prevIsOpenLicenseModalRef.current = isOpenLicenseModal;
	}, [isOpenLicenseModal, serial?.licenseManagers]);

	useEffect(() => {
		setAdmins(accountAdmins);
	}, [accountAdmins]);

	// Sync licenseManagers from fresh serialData.pool after refetch
	useEffect(() => {
		if (serialData?.pool?.licenseManagers && !isSerialDataLoading) {
			const freshManagers = serialData.pool.licenseManagers;
			const nonAdmins = freshManagers.filter(m => !m.isAdmin);
			setLicenseManagers(nonAdmins);
		}
	}, [serialData?.pool?.licenseManagers, isSerialDataLoading]);

	useEffect(() => {
		if (isOpenLicenseModal) {
			setSelectedCM(currentCMAssignment);
			setLastCommittedCM(currentCMAssignment);
			hadCMOperationsRef.current = false;
			pendingLinkAccountName.current = '';
			// Seed committedLinkIdRef from the prop so the very first unlink (no prior link in session) works
			committedLinkIdRef.current = linkedPoolId;
			// Reset pagination when modal opens
			setOutboundPage(1);
			setDateFilters({});
		}
	}, [isOpenLicenseModal, currentCMAssignment]);

	useEffect(() => {
		if (!isEmpty(serial) && isOpenLicenseModal) {
			// Only refetch on initial modal open — skip if any operation (LM or CM)
			// has already happened; all query invalidation is deferred to modal close.
			if (!hadOperationsRef.current && !hadCMOperationsRef.current) {
				serialDataRefetch();
			}
		}
	}, [serial, isOpenLicenseModal]);

	useEffect(() => {
		if (serialData) {
			const alias = serialData.pool?.alias || serialData.pool?.name;
			setAliasValue(alias);
			setAliasDefault(alias);
			// Mark initial load as complete once data is loaded
			setIsInitialModalLoad(false);
		}
	}, [serialData]);

	useEffect(() => {
		if (isOpenLicenseModal) {
			// If we've had LM operations, preserve local state (don't overwrite with stale serial prop)
			// The serial prop is stale until /licenses refetches on modal close
			if (!hadOperationsRef.current) {
				// No operations yet - sync from /licenses response
				const latestNotificationEmails = (serial.notification_contacts ?? '').split(';').filter(Boolean);
				form.setFieldValue("contacts", latestNotificationEmails);
			}
			// else: Keep current form state (reflects LM operations)

			form.setFieldValue("threshold", serial.pool_balance_threshold);

			const currentThreshold = serial?.pool_balance_threshold;
			if (isNil(currentThreshold)) {
				form.setFieldValue("threshold", 0);
			}

			// Reset all unsaved changes flags when modal opens
			setHasUnsavedChanges(false);
			setHasNotificationFormChanges(false);
			setAliasError(null);
			setIsLoadingContacts(false);
			setIsInitialModalLoad(true);
		}

		if (!isOpenLicenseModal) {
			form.reset();
			setIsLoadingContacts(false);
			setIsInitialModalLoad(true);
			// Had operations — reset flag after /licenses refetch completes on modal close
			if (hadOperationsRef.current) {
				setTimeout(() => {
					hadOperationsRef.current = false;
					lastLMOperationWasAssignment.current = false;
					lastNotificationUpdateWasManual.current = false;
				}, 500); // Give time for /licenses refetch to complete
			}
		}
	}, [isOpenLicenseModal]);

	useEffect(() => {
		if (thresholdValue === 0 && contactsError === MSG.VALIDATE.REQUIRED("Notification Contacts")) {
			form.clearErrors();
		}
	}, [contactsError, thresholdValue]);

	const handleRemoveLicenseManager = (managerEmail: string) => {
		const currentManagerEmails = licenseManagers.map(m => getManagerEmail(m)).filter(Boolean) as string[];
		setManagerToRemove(managerEmail);
		setLoadingRemoveManagerId(managerEmail);
		setIsLoadingContacts(true);
		removeManagerMutate({
			poolUuid: typeof serial.serialNumber === 'string' ? serial.serialNumber : serial.serialNumber?.poolId,
			licenseManagerEmail: managerEmail,
			currentManagers: currentManagerEmails,
		});
	};

	const handleAddMoreManagersWrapper = (managers: LicenseManager[]) => {
		setPendingManagersToAdd(managers);
		setIsLoadingContacts(true);
		if (onAddMoreManagers) {
			onAddMoreManagers(managers);
		}
	};

	const handleRemoveAllLicenseManagers = () => {
		const regularManagers = licenseManagers.filter(m => !m.isAdmin);
		if (regularManagers.length === 0) return;

		setIsRemovingAll(true);
		setLicenseManagers([]);
		setIsLoadingContacts(true);

		if (onDirectAssignManagers) {
			onDirectAssignManagers([typeof serial.serialNumber === 'string' ? serial.serialNumber : serial.serialNumber?.poolId], []);
		}
	};

	useEffect(() => {
		if (removeManagerSuccess && managerToRemove) {
			hadOperationsRef.current = true;
			lastLMOperationWasAssignment.current = false;
			setLicenseManagers(prev => prev.filter(m => getManagerEmail(m) !== managerToRemove));

			// Always update notification contacts when removing a manager
			const currentContacts = form.values.contacts || [];
			const updatedContacts = currentContacts.filter(c => c !== managerToRemove);

			// Update local state
			form.setFieldValue("contacts", updatedContacts);

			const poolId = typeof serial.serialNumber === 'string' ? serial.serialNumber : serial.serialNumber?.poolId;


			isNotificationUpdateFromLMOperation.current = true;
			notificationUpdateMutate({
				serial: poolId,
				contacts: updatedContacts,
				threshold: form.values.threshold,
				prevThreshold: serial?.pool_balance_threshold,
				userName: Cookies?.get("user_name"),
			});

			setManagerToRemove(null);
		}
	}, [removeManagerSuccess, managerToRemove]);

	useEffect(() => {
		if (removeManagerError) {
			setManagerToRemove(null);
			setLoadingRemoveManagerId(null);
			setIsRemovingAll(false);
			setIsLoadingContacts(false);
		}
	}, [removeManagerError]);

	useEffect(() => {
		if (assignManagersSuccess) {
			hadOperationsRef.current = true;
			lastLMOperationWasAssignment.current = true;
			setIsRemovingAll(false);

			if (pendingManagersToAdd.length > 0) {
				const existingEmails = licenseManagers.map(m => getManagerEmail(m)?.toLowerCase()).filter(Boolean);
				const newManagers = pendingManagersToAdd.filter(m => {
					const email = getManagerEmail(m);
					return email && !existingEmails.includes(email.toLowerCase());
				});
				const allNonAdminManagers = [...licenseManagers, ...newManagers].sort((a, b) =>
					(a.name || '').localeCompare(b.name || '')
				);
				setLicenseManagers(allNonAdminManagers);

				// Update notification contacts locally for immediate UI feedback
				// SF is the source of truth and handles backend updates via email sending API
				// The actual contacts will be synced from /licenses response after modal close/reopen
				const newLMEmails = pendingManagersToAdd
					.map(m => getManagerEmail(m))
					.filter(Boolean) as string[];

				const currentContacts = form.values.contacts || [];
				const updatedContacts = [...currentContacts];

				// Add new LM emails to contacts if not already present
				newLMEmails.forEach(email => {
					const emailLower = email.toLowerCase();
					const exists = updatedContacts.some(c => c.toLowerCase() === emailLower);
					if (!exists) {
						updatedContacts.push(email);
					}
				});

				// Update form for immediate display (no API call - SF handles backend)
				if (updatedContacts.length > currentContacts.length) {
					form.setFieldValue("contacts", updatedContacts);
				}

				setPendingManagersToAdd([]);
			}
			// Don't stop loading here - wait for modal to close
		}
	}, [assignManagersSuccess, pendingManagersToAdd]);

	// Stop loading when assignment modal closes
	useEffect(() => {
		if (assignManagersSuccess && !isAssigningManagers && isLoadingContacts) {
			// Modal closed after successful assignment
			setIsLoadingContacts(false);
		}
	}, [assignManagersSuccess, isAssigningManagers, isLoadingContacts]);

	// Update notification contacts from refetched serial data
	useEffect(() => {
		// For LM assignment: allow sync from /licenses refetch (SF updates contacts via email API)
		// For LM removal: block sync (removal triggers update-notification API, keep local state)
		// For manual updates: block sync (keep local form state until modal close/reopen)
		const allowSyncAfterAssignment = hadOperationsRef.current && lastLMOperationWasAssignment.current;
		const blockSyncAfterManualUpdate = hadOperationsRef.current && lastNotificationUpdateWasManual.current;
		if (serialData?.pool && !isSerialDataLoading && !isLoadingContacts && !isUpdatingNotification && loadingRemoveManagerId === null && (!hadOperationsRef.current || allowSyncAfterAssignment) && !blockSyncAfterManualUpdate) {
			const notificationEmails = (serial.notification_contacts ?? '').split(';').filter(Boolean);
			const uniqueEmails = Array.from(
				new Set(notificationEmails.map(email => email.toLowerCase()))
			).map(lowerEmail =>
				notificationEmails.find(email => email.toLowerCase() === lowerEmail)
			).filter(Boolean);

			form.setFieldValue("contacts", uniqueEmails);
		}
	}, [serialData, isSerialDataLoading, serial?.notification_contacts, isUpdatingNotification, loadingRemoveManagerId]);

	// Set loading state when licenses are fetching
	useEffect(() => {
		if (isLicensesFetching && isOpenLicenseModal) {
			setIsLoadingContacts(true);
		} else if (!isLicensesFetching && !isAssigningManagers && !removeManagerSuccess) {
			setIsLoadingContacts(false);
		}
	}, [isLicensesFetching, isOpenLicenseModal, isAssigningManagers, removeManagerSuccess]);


	const handleCMChange = (value: string | null) => {
		const newValue = value || '';
		setSelectedCM(newValue);
		// Compare against last committed value (not stale currentCMAssignment prop)
		const cmChanged = newValue !== lastCommittedCM;
		handleFormChange({ threshold: form.values.threshold, contacts: form.values.contacts, isCMChanged: cmChanged });
	};

	const handleCMAssignment = async () => {
		if (!serial?.serialNumber) return;

		const poolId = typeof serial.serialNumber === 'string' ? serial.serialNumber : serial.serialNumber?.poolId;
		const aId = Cookies.get("aId");

		if (!selectedCM && lastCommittedCM) {
			// Use the linkId captured from the last successful link response; fall back to the
			// prop for the very first unlink (no prior link has happened in this session).
			const activeLinkId = committedLinkIdRef.current || linkedPoolId;
			if (!activeLinkId) {
				return;
			}
			setIsUpdatingCM(true);
			unlinkCMMutate({ linkId: activeLinkId });
			return;
		}

		// Scenario 2: Assigning/Changing CM → Link (also covers re-link via Mule)
		if (selectedCM && (selectedCM !== lastCommittedCM)) {
			const selectedAccount = availableAccounts.find(acc => acc.name === selectedCM);

			if (!selectedAccount?.accountId) {
				return;
			}

			setIsUpdatingCM(true);
			pendingLinkAccountName.current = selectedCM;
			try {
				const result = await linkCMMutateAsync({
					sfAccountId: selectedAccount.accountId,
					targetPools: [poolId],
					aId: aId,
				});
				// result is an AxiosResponse — server body is at result.data.
				// Server shape: { status, data: { pools: [{poolId, linkId}] }, msg }
				committedLinkIdRef.current = (result as any)?.data?.data?.pools?.[0]?.linkId || '';
			} catch {
				// isLinkError effect handles the revert
			}
			return;
		}
	};

	useEffect(() => {
		if (isUnlinkSuccess) {
			setSelectedCM('');
			setLastCommittedCM('');
			hadCMOperationsRef.current = true;
			setIsUpdatingCM(false);
			// Clear unsaved changes flag after successful CM unlink
			setHasUnsavedChanges(false);
			// Notify parent that CM is now saved (no longer changed)
			handleFormChange({ threshold: form.values.threshold, contacts: form.values.contacts, isCMChanged: false });
		}
	}, [isUnlinkSuccess]);

	useEffect(() => {
		if (isLinkSuccess) {
			// committedLinkIdRef is already set by the mutateAsync call in handleCMAssignment
			setLastCommittedCM(pendingLinkAccountName.current);
			hadCMOperationsRef.current = true;
			pendingLinkAccountName.current = '';
			setIsUpdatingCM(false);
			setHasUnsavedChanges(false);
			handleFormChange({ threshold: form.values.threshold, contacts: form.values.contacts, isCMChanged: false });
		}
	}, [isLinkSuccess]);

	useEffect(() => {
		if (isUnlinkError || isLinkError) {
			setIsUpdatingCM(false);
			// Revert to last committed value (not stale prop) so a second attempt works correctly
			setSelectedCM(lastCommittedCM);
		}
	}, [isUnlinkError, isLinkError, lastCommittedCM]);

	const executeAliasUpdate = () => {
		const poolId = typeof serial.serialNumber === 'string' ? serial.serialNumber : serial.serialNumber?.poolId;
		const resolvedAlias = aliasValue.trim() || serialData?.pool?.name;
		submittedAliasRef.current = resolvedAlias; // Capture the value being submitted

		aliasUpdateMutate({
			serial: poolId,
			alias: resolvedAlias,
			prevAlias: aliasDefault,
			userName: Cookies?.get("user_name"),
		});
	};

	const handleAliasSubmit = () => {
		// If the field is empty, warn the user that the name will reset to the original
		if (!aliasValue.trim()) {
			setShowAliasResetConfirm(true);
			return;
		}
		executeAliasUpdate();
	};

	const handleNotificationSubmit = (data: FormData) => {
		setIsSubmitting(true);
		// Mark as manual notification update (not from LM operation)
		hadOperationsRef.current = true; // Set immediately to prevent sync effect race condition
		lastNotificationUpdateWasManual.current = true;
		const poolId = typeof serial.serialNumber === 'string' ? serial.serialNumber : serial.serialNumber?.poolId;

		notificationUpdateMutate({
			serial: poolId,
			contacts: data.contacts || [],
			threshold: data.threshold,
			prevThreshold: serial?.pool_balance_threshold,
			userName: Cookies?.get("user_name"),
		});

		setHasUnsavedChanges(false);
	};

	useEffect(() => {
		if (aliasUpdateSuccess || aliasUpdateError) {
			if (aliasUpdateSuccess && aliasUpdateData.data?.status == "success") {
				hadOperationsRef.current = true;
				setAliasDefault(submittedAliasRef.current);
				setAliasValue(submittedAliasRef.current); // Show resolved name immediately (handles empty → pool name reset)
				serialDataRefetch(); // Refresh PAM data so serialData effect stays in sync
				setHasUnsavedChanges(false);
				// Notify parent that alias is now saved (no longer changed)
				handleFormChange({ threshold: form.values.threshold, contacts: form.values.contacts, aliasChanged: false });
			}
		}
	}, [aliasUpdateSuccess, aliasUpdateError]);

	useEffect(() => {
		if (notificationUpdateSuccess || notificationUpdateError) {
			if (notificationUpdateSuccess && notificationUpdateData.data?.status == "success") {
				hadOperationsRef.current = true;
				setHasUnsavedChanges(false);
				setHasNotificationFormChanges(false);
			}
			setIsSubmitting(false);

			// If notification update was from LM operation, stop loading and clear removal state
			if (isNotificationUpdateFromLMOperation.current) {
				setIsLoadingContacts(false);
				setLoadingRemoveManagerId(null);
				isNotificationUpdateFromLMOperation.current = false;
			}
		}
	}, [notificationUpdateSuccess, notificationUpdateError]);

	useEffect(() => {
		if (!isOpenLicenseModal && onModalClose) {
			onModalClose(hadOperationsRef.current || hadCMOperationsRef.current);
			hadCMOperationsRef.current = false;
		}
	}, [isOpenLicenseModal, onModalClose]);

	// Report LM operation state to parent to prevent modal closing
	useEffect(() => {
		const isLMOperationInProgress = isAssigningManagers || loadingRemoveManagerId !== null || isUpdatingNotification;
		if (onLMOperationChange) {
			onLMOperationChange(isLMOperationInProgress);
		}
	}, [isAssigningManagers, loadingRemoveManagerId, isUpdatingNotification, onLMOperationChange]);

	const accountOptions = availableAccounts.map(acc => acc.name);
	// Only show full modal skeleton on initial load, not during refetches
	const isModalLoading = isSerialDataLoading && isInitialModalLoad;
	// Disable LM operations while any LM operation is in progress to prevent race conditions and state inconsistencies
	const isLMOperationDisabled = isSerialDataLoading || isAssigningManagers || loadingRemoveManagerId !== null || isUpdatingNotification;
	const isCMOperationInProgress = isUpdatingCM;

	// Pagination handlers for outbound allocations - auto-refetches when state changes
	const handleOutboundPageChange = (newPage: number) => {
		setOutboundPage(newPage);
	};

	const handleOutboundDateFilterChange = (filters: { date_gte?: string; date_lte?: string }) => {
		setDateFilters(filters);
		setOutboundPage(1); // Reset to page 1 when filters change
	};

	return (
		<>
			<Grid>
				<Grid.Col span={{ md: 4 }}>
					{isModalLoading && <Skeleton />}
					{!isModalLoading && serialData && serialData.pool && (
						<Stack gap="sm">
							<TextInput
								variant="unstyled"
								label="Account"
								value={serial.accountName}
								readOnly
							/>
							<TextInput
								variant="unstyled"
								label="Pool ID"
								value={serialData.pool.uuid}
								readOnly
							/>
							{isEditable && (
								<>
									<Autocomplete
										label={
											<Tooltip label={disableCMAssignmentForPool?.(serial) ? MSG.HELP.ALLOCATION_FROM_PARENT : MSG.HELP.ACCOUNT_ALLOCATION}>
												<Flex justify="space-between" align="center" gap="xs" mt={0}>
													Assign to (Dante product development partner)
													<FaQuestionCircle />
												</Flex>
											</Tooltip>
										}
										placeholder="Search Dante product development partner account..."
										data={accountOptions}
										value={selectedCM}
										onChange={handleCMChange}
										clearable
										variant="default"
										size="sm"
										radius="sm"
										disabled={disableCMAssignmentForPool?.(serial) || !isAllowedAllocation || isCMOperationInProgress}
									/>
									<Flex justify="flex-end" mt={0}>
										<Button
											size="xs"
											onClick={handleCMAssignment}
											disabled={isCMOperationInProgress || selectedCM === lastCommittedCM || disableCMAssignmentForPool?.(serial)}
											loading={!disableCMAssignmentForPool?.(serial) && isCMOperationInProgress}
										>
											Save
										</Button>
									</Flex>
								</>
							)}
							{(Cookies.get("user_permission")
								.split("-")
								.includes("licenseThresholdManagement") &&
								isEditable && (
									<>
										<TextInput
											label={
												<Tooltip multiline w={400} label={MSG.HELP.POOL_NAME}>
													<Flex justify="space-between" align="center" gap="xs" mt={0}>
														License Pool Name
														<FaQuestionCircle />
													</Flex>
												</Tooltip>
											}
											placeholder="License Pool Name"
											disabled={isUpdatingAlias}
											value={aliasValue}
											error={aliasError}
											onChange={(e) => {
												const newValue = e.target.value;
												setAliasValue(newValue);
												// Validate character limit
												if (newValue.length > aliasMaxLength) {
													setAliasError(`License Pool Name cannot exceed ${aliasMaxLength} characters`);
												} else {
													setAliasError(null);
												}
												// Track unsaved alias changes
												const aliasChanged = newValue !== aliasDefault;
												handleFormChange({ threshold: form.values.threshold, contacts: form.values.contacts, aliasChanged });
											}}
										/>
										<Flex justify="flex-end" mt={0}>
											<Button
												size="xs"
												onClick={handleAliasSubmit}
												loading={isUpdatingAlias}
												disabled={aliasValue === aliasDefault || !!aliasError}
											>
												Save
											</Button>
										</Flex>
									</>

								)
							) || <TextInput
									variant="unstyled"
									label="License Pool Name"
									value={aliasDefault}
									readOnly
								/>}
							<TextInput
								variant="unstyled"
								label="Audinate Product"
								value={serialData.pool.productSKU.sku}
								readOnly
							/>
							<TextInput
								variant="unstyled"
								label="Pool Balance"
								value={serialData.pool.balance}
								readOnly
							/>

							<Divider my="md" />

							<LicenseManagersSection
								licenseManagers={licenseManagers}
								admins={admins}
								isEditable={isEditable && isAllowedAllocation}
								onRemoveManager={
									isEditable && isAllowedAllocation
										? handleRemoveLicenseManager
										: undefined
								}
								onRemoveAllManagers={
									isEditable && isAllowedAllocation
										? handleRemoveAllLicenseManagers
										: undefined
								}
								availableManagers={availableManagers}
								onAddManagers={
									isEditable && isAllowedAllocation && Cookies.get("user_permission")?.split("-").includes("licenseThresholdManagement")
										? handleAddMoreManagersWrapper
										: undefined
								}
								isLoading={isModalLoading}
								isOperationDisabled={isLMOperationDisabled}
								isAssigningManagers={isAssigningManagers}
								loadingRemoveManagerId={loadingRemoveManagerId}
								isRemovingAll={isRemovingAll}
							/>
							<form onSubmit={form.onSubmit((values) => handleNotificationSubmit(values))}>
								{(isEditable && Cookies.get("user_permission")?.split("-").includes("licenseThresholdManagement")) && (
									<>
										<NumberInput
											key={form.key("threshold")}
											label={
												<Tooltip multiline w={400} label={MSG.HELP.NOTIFICATION_THRESHOLD}>
													<Flex justify="space-between" align="center" gap="xs" mt="0">
														Notification Threshold
														<FaQuestionCircle />
													</Flex>
												</Tooltip>
											}
											placeholder="Enter Notification Threshold"
											disabled={isSubmitting}
											min={0}
											step={1}
											my="md"
											{...form.getInputProps("threshold")}
											onChange={(value) => {
												form.getInputProps("threshold").onChange(value);
												const adminAndLMEmails = serial?.licenseManagers?.map(m => getManagerEmail(m)).filter(Boolean) || [];
												const storedContacts = serial?.notification_contacts?.split(";").filter(c => c && !adminAndLMEmails.includes(c)) || [];
												const isFormModified = (value != serial?.pool_balance_threshold && !isNil(serial?.pool_balance_threshold)) || form.values.contacts.join(";") !== storedContacts.join(";");
												setHasNotificationFormChanges(isFormModified);
												handleFormChange({ threshold: value, contacts: form.values.contacts });
											}}
										/>
										<PillsInput
											label="Notification Contacts"
											disabled={isSubmitting || isLoadingContacts}
											error={form.errors.contacts}
										>
											{isLoadingContacts ? (
												<MantineSkeleton height={36} />
											) : (
												<Pill.Group>
													{(form.values.contacts || []).map((email, index) => {

														const isLmEmail = lmEmails.includes(email.toLowerCase());

														return (
															<Pill
																key={index}
																withRemoveButton={!isLmEmail}
																onRemove={() => {
																	const updatedContacts = (form.values.contacts || []).filter((_, i) => i !== index);
																	form.setFieldValue("contacts", updatedContacts);
																	const adminAndLMEmails = serial?.licenseManagers?.map(m => getManagerEmail(m)).filter(Boolean) || [];
																	const storedContacts = serial?.notification_contacts?.split(";").filter(c => c && !adminAndLMEmails.includes(c)) || [];
																	const isFormModified = (form.values.threshold != serial?.pool_balance_threshold && !isNil(serial?.pool_balance_threshold)) || updatedContacts.join(";") !== storedContacts.join(";");
																	setHasNotificationFormChanges(isFormModified);
																	handleFormChange({ threshold: form.values.threshold, contacts: updatedContacts });
																}}
															>
																{email}
															</Pill>
														);
													})}
													<PillsInput.Field
														placeholder="Type an email and press Enter"
														onKeyDown={(e) => {
															if (e.key === 'Enter') {
																e.preventDefault();
																const input = e.currentTarget;
																const value = input.value.trim();
																if (value && !form.values.contacts?.includes(value)) {
																	const updatedContacts = [...(form.values.contacts || []), value];
																	form.setFieldValue("contacts", updatedContacts);
																	const adminAndLMEmails = serial?.licenseManagers?.map(m => getManagerEmail(m)).filter(Boolean) || [];
																	const storedContacts = serial?.notification_contacts?.split(";").filter(c => c && !adminAndLMEmails.includes(c)) || [];
																	const isFormModified = (form.values.threshold != serial?.pool_balance_threshold && !isNil(serial?.pool_balance_threshold)) || updatedContacts.join(";") !== storedContacts.join(";");
																	setHasNotificationFormChanges(isFormModified);
																	handleFormChange({ threshold: form.values.threshold, contacts: updatedContacts });
																	input.value = '';
																}
															}
														}}
													/>
												</Pill.Group>
											)}
										</PillsInput>
										<Text size="xs" c="dimmed" mt="xs">
											Note: Assigned License Managers will also receive notifications related to this pool.
										</Text>
										<ModalButtons>
											<Button type="submit" loading={isSubmitting} disabled={!hasNotificationFormChanges}>Update</Button>
										</ModalButtons>
									</>
								)}
								{!(isEditable && Cookies.get("user_permission")?.split("-").includes("licenseThresholdManagement")) && (
									<>
										<TextInput
											variant="unstyled"
											label="Notification Threshold"
											value={serial.pool_balance_threshold || "-"}
											readOnly
										/>
										<PillsInput label="Notification Contacts">
											<Pill.Group>
												{(serial.notification_contacts &&
													serial.notification_contacts.split(";")
														.map((email, key) => (
															<Pill key={key}>{email}</Pill>
														))) ||
													""}
											</Pill.Group>
										</PillsInput>
									</>
								)}
							</form>
						</Stack>
					)}
				</Grid.Col>
				<Grid.Col span={{ md: 8 }}>
					<Group>
						<Divider orientation="vertical" size="xs" />
						<Text component="div" style={{ flex: 1 }}>
							<TableBounds
								serialData={serialData}
								isSerialDataLoading={isModalLoading}
								isOpen={isOpenLicenseModal}
								outboundData={outboundData}
								isOutboundLoading={isOutboundLoading}
								outboundPage={outboundPage}
								onOutboundPageChange={handleOutboundPageChange}
								onOutboundDateFilterChange={handleOutboundDateFilterChange}
							/>
						</Text>
					</Group>
				</Grid.Col>
			</Grid>
			<Modal
				opened={showAliasResetConfirm}
				onClose={() => setShowAliasResetConfirm(false)}
				withCloseButton={false}
				centered
				size="sm"
				radius="md"
				padding="xl"
			>
				<Box ta="center" py="xs">
					<ThemeIcon color="orange" variant="light" size={44} radius="xl" mb="sm">
						<FaExclamationCircle size={22} />
					</ThemeIcon>
					<Text fw={700} size="lg" mb={6}>Reset pool name?</Text>
					<Text size="sm" c="dimmed" mb="sm" lh={1.6}>
						Saving with an empty name will restore the original pool name:
					</Text>
					<Box
						bg="gray.0"
						p="sm"
						mb="lg"
						style={{ borderRadius: 8, border: '1px solid var(--mantine-color-gray-3)' }}
					>
						<Text size="sm" fw={600} c="dark">{serialData?.pool?.name}</Text>
					</Box>
					<Group justify="center" gap="sm" grow>
						<Button variant="default" onClick={() => setShowAliasResetConfirm(false)}>Cancel</Button>
						<Button
							color="orange"
							onClick={() => {
								setShowAliasResetConfirm(false);
								executeAliasUpdate();
							}}
						>
							Reset name
						</Button>
					</Group>
				</Box>
			</Modal>
		</>
	);
};

export default ViewLicense;
